import { Injectable } from '@angular/core';
import { Observable, of, BehaviorSubject, Subject } from 'rxjs';
import { tap, catchError, map } from 'rxjs/operators';
import { Api } from './api';
import {
  AppRole,
  AppMenuItem,
  AppRolePermission,
  UserEffectivePermissions,
} from '../models/app-role.model';

@Injectable({ providedIn: 'root' })
export class RoleManagementService {

  /** Cached permissions for current user */
  private _myPermissions$ = new BehaviorSubject<UserEffectivePermissions>({});
  readonly myPermissions$ = this._myPermissions$.asObservable();

  /** Emitted when menu items are created/updated/deleted or reordered */
  readonly menuOrderChanged$ = new Subject<void>();

  constructor(private api: Api) {}

  // ─── Roles ──────────────────────────────────────────────────────────────────

  getRoles(): Observable<AppRole[]> {
    return this.api.get('/admin/app-roles');
  }

  getRole(id: number): Observable<AppRole> {
    return this.api.get(`/admin/app-roles/${id}`);
  }

  createRole(role: AppRole): Observable<AppRole> {
    return this.api.post('/admin/app-roles', role);
  }

  updateRole(id: number, role: AppRole): Observable<AppRole> {
    return this.api.putGenerate<AppRole>(`/admin/app-roles/${id}`, role);
  }

  deleteRole(id: number): Observable<void> {
    return this.api.delete<void>(`/admin/app-roles/${id}`);
  }

  // ─── Permissions ─────────────────────────────────────────────────────────

  getRolePermissions(roleId: number): Observable<AppRolePermission[]> {
    return this.api.get(`/admin/app-roles/${roleId}/permissions`);
  }

  saveRolePermissions(roleId: number, perms: AppRolePermission[]): Observable<AppRolePermission[]> {
    return this.api.putGenerate<AppRolePermission[]>(`/admin/app-roles/${roleId}/permissions`, perms);
  }

  // ─── User-Role assignment ─────────────────────────────────────────────────

  getUserRoles(login: string): Observable<AppRole[]> {
    return this.api.get(`/admin/users/${login}/app-roles`);
  }

  setUserRoles(login: string, roleIds: number[]): Observable<void> {
    return this.api.putGenerate<void>(`/admin/users/${login}/app-roles`, roleIds);
  }

  // ─── Menu items ───────────────────────────────────────────────────────────

  /** Get all active menu items sorted by sortOrder (for sidebar population) */
  getActiveMenuItems(): Observable<AppMenuItem[]> {
    return this.api.get('/app-menu-items');
  }

  /** Get all menu items (admin only) */
  getMenuItems(): Observable<AppMenuItem[]> {
    return this.api.get('/admin/app-menu-items');
  }

  /** Create a new menu item */
  createMenuItem(item: Partial<AppMenuItem>): Observable<AppMenuItem> {
    return this.api.post('/admin/app-menu-items', item);
  }

  /** Update an existing menu item */
  updateMenuItem(id: number, item: Partial<AppMenuItem>): Observable<AppMenuItem> {
    return this.api.putGenerate<AppMenuItem>(`/admin/app-menu-items/${id}`, item);
  }

  /** Delete a menu item */
  deleteMenuItem(id: number): Observable<void> {
    return this.api.delete<void>(`/admin/app-menu-items/${id}`);
  }

  /** Reorder menu items by sortOrder */
  reorderMenuItems(orders: { itemKey: string; sortOrder: number }[]): Observable<void> {
    return this.api.putGenerate<void>('/admin/app-menu-items/reorder', orders);
  }

  /** Get a short-lived SSO token for the chatbot (Open WebUI) */
  getChatbotSsoToken(): Observable<{ token: string; url: string }> {
    return this.api.get('/chatbot/sso-token');
  }

  // ─── Effective permissions for current user ───────────────────────────────

  loadMyPermissions(): Observable<UserEffectivePermissions> {
    return this.api.get('/app-roles/my-permissions').pipe(
      map((perms: AppRolePermission[]) => {
        const result: UserEffectivePermissions = {};
        perms.forEach(p => { result[p.menuItemKey] = p; });
        return result;
      }),
      tap(result => this._myPermissions$.next(result)),
      catchError(() => {
        this._myPermissions$.next({});
        return of({} as UserEffectivePermissions);
      })
    );
  }

  /** Check if current user can view a specific menu item key */
  canView(itemKey: string): boolean {
    const perms = this._myPermissions$.getValue();
    return !!perms[itemKey]?.canView;
  }

  canCreate(itemKey: string): boolean {
    const perms = this._myPermissions$.getValue();
    return !!perms[itemKey]?.canCreate;
  }

  canEdit(itemKey: string): boolean {
    const perms = this._myPermissions$.getValue();
    return !!perms[itemKey]?.canEdit;
  }

  canDelete(itemKey: string): boolean {
    const perms = this._myPermissions$.getValue();
    return !!perms[itemKey]?.canDelete;
  }

  canExport(itemKey: string): boolean {
    const perms = this._myPermissions$.getValue();
    return !!perms[itemKey]?.canExport;
  }
}
